import { Input } from "@/common/components/ui/input";
import { Label } from "@/common/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/common/components/ui/select";
import { Separator } from "@/common/components/ui/separator";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/common/components/ui/card";
import { ShieldCheck, AlertCircle, Layers } from "lucide-react";
import { TabProps } from "../types";

interface OfferingTabProps extends TabProps {
  validationErrors?: Record<string, string>;
}

const OfferingTab = ({
  formData,
  handleInputChange,
  handleSelectChange,
  validationErrors = {},
  readOnly = false,
}: OfferingTabProps) => {
  const readOnlyClass = readOnly ? "bg-slate-50 cursor-not-allowed" : "";
  const formatIndianAmount = (value: string | number | null | undefined) => {
    const num = Number(value ?? 0);
    return Number.isNaN(num) ? "0" : num.toLocaleString("en-IN");
  };
  const formatIndianNumberInput = (
    value: string | number | null | undefined,
    allowDecimal = true
  ) => {
    const raw = String(value ?? "").replace(/,/g, "");
    if (!raw || raw === "-" || raw === "." || raw === "-.") {
      return raw;
    }

    const [intPart, decimalPart] = raw.split(".");
    const isNegative = intPart.startsWith("-");
    const absInt = intPart.replace("-", "");
    if (!/^\d+$/.test(absInt)) {
      return String(value ?? "");
    }

    const grouped = Number(absInt).toLocaleString("en-IN");
    const withSign = isNegative ? `-${grouped}` : grouped;
    if (!allowDecimal || decimalPart === undefined) {
      return withSign;
    }
    return `${withSign}.${decimalPart}`;
  };
  return (
    <div className="space-y-6">
      {/* Basic Configuration Section */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100 bg-slate-50/50 px-6 py-4">
          <CardTitle className="flex items-center gap-2 text-sm font-bold text-slate-800">
            <Layers className="h-4 w-4 text-primary" />
            Basic Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-6 p-6 md:grid-cols-4">
          <div className="space-y-2">
            <Label className="text-[12px] font-bold uppercase tracking-wider text-slate-400">
              IPO Name
            </Label>
            <Input
              name="ipo_name"
              value={formData?.ipo_name}
              onChange={handleInputChange}
              disabled={readOnly}
              placeholder={formData.ipo_name || "Enter IPO Name"}
              className={`h-10 transition-all placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass} ${
                validationErrors.ipo_name
                  ? "border-red-300 focus:border-red-500"
                  : ""
              }`}
            />
            {validationErrors.ipo_name && (
              <p className="text-xs text-red-600">
                {validationErrors.ipo_name}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-[12px] font-bold uppercase tracking-wider text-slate-400">
              ISIN Number
            </Label>
            <Input
              name="isin"
              value={formData.isin}
              onChange={handleInputChange}
              disabled={readOnly}
              placeholder={formData.isin || "Enter ISIN Number"}
              className={`h-10 transition-all placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass} ${
                validationErrors.isin
                  ? "border-red-300 focus:border-red-500"
                  : ""
              }`}
            />
          </div>
          {/* <div className="space-y-2">
            <Label className="text-[12px] font-bold uppercase tracking-wider text-slate-400">
              NSE Symbol
            </Label>
            <Input
              name="nse_symbol"
              value={formData.nse_symbol}
              onChange={handleInputChange}
              disabled={readOnly}
              placeholder={formData.nse_symbol || "NSE: SYMBOL"}
              className={`placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 h-10 transition-all ${readOnlyClass} ${
                validationErrors.nse_symbol
                  ? "border-red-300 focus:border-red-500"
                  : ""
              }`}
            />
            {validationErrors.nse_symbol && (
              <p className="text-xs text-red-600">
                {validationErrors.nse_symbol}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-[12px] font-bold uppercase tracking-wider text-slate-400">
              BSE Symbol
            </Label>
            <Input
              name="bse_symbol"
              value={formData.bse_symbol}
              onChange={handleInputChange}
              disabled={readOnly}
              placeholder={formData.bse_symbol || "BSE: 532134"}
              className={`placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 h-10 transition-all ${readOnlyClass} ${
                validationErrors.bse_symbol
                  ? "border-red-300 focus:border-red-500"
                  : ""
              }`}
            />
          </div> */}
          <div className="space-y-2">
            <Label className="text-[12px] font-bold uppercase tracking-wider text-slate-400">
              IPO Logo
            </Label>
            <Input
              name="ipo_logo"
              value={formData.ipo_logo}
              onChange={handleInputChange}
              disabled={readOnly}
              placeholder={formData.ipo_logo || "Enter IPO Logo"}
              className={`h-10 transition-all placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass} ${
                validationErrors.ipo_logo
                  ? "border-red-300 focus:border-red-500"
                  : ""
              }`}
            />
            {validationErrors.nse_symbol && (
              <p className="text-xs text-red-600">
                {validationErrors.nse_symbol}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-[12px] font-bold uppercase text-slate-400">
              IPO Sub Type
            </Label>
            <Select
              name="ipo_type"
              value={formData.ipo_type}
              onValueChange={value => handleSelectChange?.("ipo_type", value)}
              disabled={readOnly}
            >
              <SelectTrigger className={readOnlyClass}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Main Board">Main Board</SelectItem>
                <SelectItem value="SME">SME</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* <div className="space-y-2">
            <Label className="text-[12px] font-bold uppercase text-slate-400">
              Listing Status
            </Label>
            <Select
              name="status"
              value={formData.status}
              onValueChange={value =>
                handleSelectChange && handleSelectChange("status", value)
              }
              disabled={readOnly}
            >
              <SelectTrigger className={readOnlyClass}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Upcoming">Upcoming</SelectItem>
                <SelectItem value="Open">Open</SelectItem>
                <SelectItem value="Closed">Closed</SelectItem>
                <SelectItem value="Listed">Listed</SelectItem>
              </SelectContent>
            </Select>
          </div> */}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
        <div className="space-y-6 md:col-span-7">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="border-b border-slate-100 bg-slate-50/50 px-6 py-4">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-sm font-bold text-slate-800">
                  <ShieldCheck className="h-4 w-4 text-primary" />
                  IPO Pricing & Size
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-2 gap-x-8 gap-y-6">
                <div className="space-y-2">
                  <Label className="text-[12px] font-bold uppercase tracking-wider text-slate-400">
                    Price Band (Min)
                  </Label>
                  <Input
                    name="price_min"
                    type="number"
                    value={formData.price_min}
                    onChange={handleInputChange}
                    disabled={readOnly}
                    placeholder={formData.price_min || "0.00"}
                    min={0}
                    step="0.01"
                    className={`h-10 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass} ${
                      validationErrors.price_min
                        ? "border-red-300 focus:border-red-500"
                        : ""
                    }`}
                  />
                  {validationErrors.price_min && (
                    <p className="text-xs text-red-600">
                      {validationErrors.price_min}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-[12px] font-bold uppercase tracking-wider text-slate-400">
                    Price Band (Max)
                  </Label>
                  <Input
                    name="price_max"
                    type="number"
                    value={formData.price_max}
                    onChange={handleInputChange}
                    placeholder={formData.price_max || "0.00"}
                    min={0}
                    disabled={readOnly}
                    step="0.01"
                    className={`h-10 border-primary/20 bg-primary/5 font-bold text-primary transition-all focus:bg-white ${readOnlyClass} ${
                      validationErrors.price_max
                        ? "border-red-300 focus:border-red-500"
                        : ""
                    }`}
                  />
                  {validationErrors.price_max && (
                    <p className="text-xs text-red-600">
                      {validationErrors.price_max}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-[12px] font-bold uppercase tracking-wider text-slate-400">
                    Lot Size (Shares)
                  </Label>
                  <Input
                    name="lot_size"
                    type="number"
                    value={formData.lot_size}
                    onChange={handleInputChange}
                    disabled={readOnly}
                    placeholder={formData.lot_size || "e.g. 50"}
                    min={1}
                    className={`h-10 border-primary/20 bg-primary/5 font-bold text-primary transition-all focus:bg-white ${readOnlyClass} ${
                      validationErrors.lot_size
                        ? "border-red-300 focus:border-red-500"
                        : ""
                    }`}
                  />
                  {validationErrors.lot_size && (
                    <p className="text-xs text-red-600">
                      {validationErrors.lot_size}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-[12px] font-bold uppercase tracking-wider text-slate-400">
                    Face Value (₹)
                  </Label>
                  <Input
                    name="face_value"
                    type="number"
                    value={formData.face_value}
                    onChange={handleInputChange}
                    disabled={readOnly}
                    placeholder={formData.face_value || "10.00"}
                    min={0}
                    step="0.01"
                    className={`h-10 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass} ${
                      validationErrors.face_value
                        ? "border-red-300 focus:border-red-500"
                        : ""
                    }`}
                  />
                  {validationErrors.face_value && (
                    <p className="text-xs text-red-600">
                      {validationErrors.face_value}
                    </p>
                  )}
                </div>
                <Separator className="col-span-2" />
                <div className="space-y-2">
                  <Label className="text-[12px] font-bold uppercase tracking-wider text-slate-400">
                    Total Issue Size
                  </Label>
                  <Input
                    name="issue_size"
                    type="text"
                    inputMode="decimal"
                    value={formatIndianNumberInput(formData.issue_size)}
                    onChange={e => {
                      const normalized = e.target.value.replace(/,/g, "");
                      if (
                        normalized !== "" &&
                        normalized !== "-" &&
                        !/^-?\d*\.?\d*$/.test(normalized)
                      ) {
                        return;
                      }

                      handleInputChange({
                        ...e,
                        target: {
                          ...e.target,
                          name: "issue_size",
                          value: normalized,
                        },
                      } as React.ChangeEvent<HTMLInputElement>);
                    }}
                    disabled={readOnly}
                    placeholder={formData.issue_size || "e.g. ₹118.00 Cr"}
                    className={`h-10 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass} ${
                      validationErrors.issue_size
                        ? "border-red-300 focus:border-red-500"
                        : ""
                    }`}
                  />
                  {validationErrors.issue_size && (
                    <p className="text-xs text-red-600">
                      {validationErrors.issue_size}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-[12px] font-bold uppercase tracking-wider text-slate-400">
                    Issue Type
                  </Label>
                  <Select
                    name="issue_type"
                    value={formData.issue_type}
                    onValueChange={value =>
                      handleSelectChange("issue_type", value)
                    }
                    disabled={readOnly}
                  >
                    <SelectTrigger className={`h-10 ${readOnlyClass}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Book Built">Book Built</SelectItem>
                      <SelectItem value="Fixed Price">Fixed Price</SelectItem>
                      <SelectItem value="EQUITY">Equity</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-6 flex gap-3 rounded-xl border border-amber-100 bg-amber-50 p-4">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
                <div className="text-xs leading-relaxed text-amber-800">
                  <span className="font-bold">Pro Tip:</span> For &quot;Book
                  Built&quot; issues, users will see the price range. For
                  &quot;Fixed Price&quot;, set Min and Max to the same value.
                </div>
              </div>
            </CardContent>
          </Card>

          {/* <Card className="border-slate-200 shadow-sm">
            <CardHeader className="border-b border-slate-100 bg-slate-50/50 px-6 py-4">
              <CardTitle className="text-sm font-bold text-slate-800">
                Shares & Capital Structure
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-6 p-6">
              <div className="space-y-2">
                <Label className="text-[12px] font-bold uppercase text-slate-400">
                  Pre-Issue Shares
                </Label>
                <Input
                  name="pre_issue_shares"
                  type="number"
                  value={formData.pre_issue_shares}
                  onChange={handleInputChange}
                  disabled={readOnly}
                  placeholder={formData.pre_issue_shares || "0"}
                  min={0}
                  className={`placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 h-9 font-mono ${readOnlyClass} ${validationErrors.pre_issue_shares
                    ? "border-red-300 focus:border-red-500"
                    : ""
                    }`}
                />
                {validationErrors.pre_issue_shares && (
                  <p className="text-xs text-red-600">
                    {validationErrors.pre_issue_shares}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-[12px] font-bold uppercase text-slate-400">
                  Post-Issue Shares
                </Label>
                <Input
                  name="post_issue_shares"
                  type="number"
                  value={formData.post_issue_shares}
                  onChange={handleInputChange}
                  disabled={readOnly}
                  placeholder={formData.post_issue_shares || "0"}
                  min={0}
                  className={`placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 h-9 font-mono ${readOnlyClass} ${validationErrors.post_issue_shares
                    ? "border-red-300 focus:border-red-500"
                    : ""
                    }`}
                />
                {validationErrors.post_issue_shares && (
                  <p className="text-xs text-red-600">
                    {validationErrors.post_issue_shares}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-[12px] font-bold uppercase text-slate-400">
                  Pre-Issue Holding (%)
                </Label>
                <Input
                  name="pre_issue_holding"
                  type="number"
                  step="0.01"
                  value={formData.pre_issue_holding}
                  onChange={handleInputChange}
                  disabled={readOnly}
                  placeholder={formData.pre_issue_holding || "0.00"}
                  min={0}
                  max={100}
                  className={`placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 h-9 ${readOnlyClass} ${validationErrors.pre_issue_holding
                    ? "border-red-300 focus:border-red-500"
                    : ""
                    }`}
                />
                {validationErrors.pre_issue_holding && (
                  <p className="text-xs text-red-600">
                    {validationErrors.pre_issue_holding}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-[12px] font-bold uppercase text-slate-400">
                  Post-Issue Holding (%)
                </Label>
                <Input
                  name="post_issue_holding"
                  type="number"
                  step="0.01"
                  value={formData.post_issue_holding}
                  onChange={handleInputChange}
                  disabled={readOnly}
                  placeholder={formData.post_issue_holding || "0.00"}
                  min={0}
                  max={100}
                  className={`placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 h-9 ${readOnlyClass} ${validationErrors.post_issue_holding
                    ? "border-red-300 focus:border-red-500"
                    : ""
                    }`}
                />
                {validationErrors.post_issue_holding && (
                  <p className="text-xs text-red-600">
                    {validationErrors.post_issue_holding}
                  </p>
                )}
              </div>
            </CardContent>
          </Card> */}
        </div>
        <div className="space-y-6 md:col-span-5">
          <div className="space-y-4 rounded-xl border border-blue-100/50 bg-blue-50/50 p-6">
            <h4 className="mb-2 text-xs font-bold text-primary">
              Investment Minimums (Real-time)
            </h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-slate-500">
                  Retail ({formData.retail_min_lot || 1}{" "}
                  {Number(formData.retail_min_lot) > 1 ? "Lots" : "Lot"})
                </span>
                <div className="text-right">
                  <div className="font-bold text-slate-800">
                    ₹{formatIndianAmount(formData.retail_min_amount)}
                  </div>
                  <div className="text-[10px] text-slate-400">
                    {formData.retail_min_shares || 0} Shares
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-slate-500">
                  sNII ({formData.snii_lots || 14}{" "}
                  {Number(formData.snii_lots) > 1 ? "Lots" : "Lot"})
                </span>
                <div className="text-right">
                  <div className="font-bold text-slate-800">
                    ₹{formatIndianAmount(formData.snii_min_investment)}
                  </div>
                  <div className="text-[10px] text-slate-400">
                    {formData.snii_min_shares || 0} Shares
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-slate-500">
                  bNII ({formData.bnii_lots || 67}{" "}
                  {Number(formData.bnii_lots) > 1 ? "Lots" : "Lot"})
                </span>
                <div className="text-right">
                  <div className="font-bold text-slate-800">
                    ₹{formatIndianAmount(formData.bnii_min_investment)}
                  </div>
                  <div className="text-[10px] text-slate-400">
                    {formData.bnii_min_shares || 0} Shares
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfferingTab;
